/**
 * 批量上架商品 Vuex 模块
 * 管理批量上架流程中的所有状态
 */
import { cloneDeep } from 'lodash'

// 模式枚举
export const MODE_ENUM = {
  BATCH: 'batch', // 批量上架（从商品列表进入）
  SINGLE: 'single', // 单个方案上架（从租赁方案列表进入）
}

// 步骤枚举
export const STEP_ENUM = {
  STEP_ONE: 0, // 配置商品信息
  STEP_TWO: 1, // 配置服务信息
  STEP_THREE: 2, // 预览发布
}

// 初始状态
const getDefaultState = () => ({
  // Session 相关
  sessionId: null,
  sessionLoading: false,
  sessionExpired: false, // Session 过期标志

  // 模式：batch / single
  mode: MODE_ENUM.BATCH,

  // 单个方案模式时的方案ID
  planId: null,

  // 当前步骤
  currentStep: STEP_ENUM.STEP_ONE,

  // 第一步：全局车型信息
  globalCarInfo: {
    carAge: null, // 车龄
    mileage: null, // 表显里程
    hasLicense: false, // 有车证
  },

  // 第一步：车型列表相关
  carModelList: [], // 车型列表数据
  selectedModelIds: [], // 已选中的车型ID列表

  // 第一步：单个方案模式 - 方案详情（只读）
  planDetail: null,

  // 第二步：签约主体
  enterpriseId: null,
  enterpriseInfo: null,
  enterpriseList: [], // 企业列表
  cooperationPlatform: [], // 合作平台列表

  // 第二步：门店选择
  storeList: [], // 门店列表
  storeSelectAll: true, // 是否全选（默认全选）
  storeExcludeIds: [], // 全选时排除的门店ID列表
  storeSelectedIds: [], // 非全选时选中的门店ID列表

  // 第三步：预览数据
  previewData: null,
  previewList: [],

  // 进入时间（用于埋点计时）
  enterTime: null,

  // 来源（用于埋点）
  source: '', // list / workbench

  // 枚举数据（全局共享，避免重复请求）
  enums: {
    carAge: [], // 车龄枚举
    carMileage: [], // 表显里程枚举
    loaded: false, // 是否已加载
  },
})

const batchPublish = {
  namespaced: true,

  state: getDefaultState(),

  getters: {
    // 是否有 session
    hasSession: (state) => !!state.sessionId,

    // 是否批量模式
    isBatchMode: (state) => state.mode === MODE_ENUM.BATCH,

    // 是否单个方案模式
    isSingleMode: (state) => state.mode === MODE_ENUM.SINGLE,

    // 是否可以进行下一步（没有 loading）
    canProceed: (state) => !state.sessionLoading,

    // 已选门店数量
    selectedStoreCount: (state) => {
      if (state.storeSelectAll) {
        return state.storeList.length - state.storeExcludeIds.length
      }
      return state.storeSelectedIds.length
    },

    // 计算配置耗时（毫秒）
    elapsedTime: (state) => {
      if (!state.enterTime) return 0
      return Date.now() - state.enterTime
    },
  },

  mutations: {
    // 重置状态
    RESET_STATE(state) {
      Object.assign(state, getDefaultState())
    },

    // 初始化状态
    INIT_STATE(state, { mode = MODE_ENUM.BATCH, planId = null, source = '' }) {
      Object.assign(state, getDefaultState())
      state.mode = mode
      state.planId = planId
      state.source = source
      state.enterTime = Date.now()
    },

    // Session 相关
    SET_SESSION_ID(state, sessionId) {
      state.sessionId = sessionId
    },
    SET_SESSION_LOADING(state, loading) {
      state.sessionLoading = loading
    },
    SET_SESSION_EXPIRED(state, expired) {
      state.sessionExpired = expired
    },

    // 步骤控制
    SET_CURRENT_STEP(state, step) {
      state.currentStep = step
    },
    NEXT_STEP(state) {
      if (state.currentStep < STEP_ENUM.STEP_THREE) {
        state.currentStep++
      }
    },
    PREV_STEP(state) {
      if (state.currentStep > STEP_ENUM.STEP_ONE) {
        state.currentStep--
      }
    },

    // 全局车型信息
    SET_GLOBAL_CAR_INFO(state, info) {
      state.globalCarInfo = { ...state.globalCarInfo, ...info }
    },
    SET_CAR_AGE(state, carAge) {
      state.globalCarInfo.carAge = carAge
    },
    SET_MILEAGE(state, mileage) {
      state.globalCarInfo.mileage = mileage
    },
    SET_HAS_LICENSE(state, hasLicense) {
      state.globalCarInfo.hasLicense = hasLicense
    },

    // 车型列表
    SET_CAR_MODEL_LIST(state, list) {
      state.carModelList = cloneDeep(list)
    },
    SET_SELECTED_MODEL_IDS(state, ids) {
      state.selectedModelIds = cloneDeep(ids)
    },
    TOGGLE_MODEL_SELECTION(state, { modelId, selected }) {
      if (selected) {
        if (!state.selectedModelIds.includes(modelId)) {
          state.selectedModelIds.push(modelId)
        }
      } else {
        state.selectedModelIds = state.selectedModelIds.filter(
          (id) => id !== modelId,
        )
      }
    },

    // 方案详情（单个方案模式）
    SET_PLAN_DETAIL(state, detail) {
      state.planDetail = cloneDeep(detail)
    },

    // 企业相关
    SET_ENTERPRISE_LIST(state, list) {
      state.enterpriseList = cloneDeep(list)
    },
    SET_ENTERPRISE(state, { id, info }) {
      state.enterpriseId = id
      state.enterpriseInfo = info ? cloneDeep(info) : null
    },
    SET_COOPERATION_PLATFORM(state, platform) {
      state.cooperationPlatform = platform || []
    },

    // 门店相关
    SET_STORE_LIST(state, list) {
      state.storeList = cloneDeep(list)
    },
    SET_STORE_SELECT_ALL(state, selectAll) {
      state.storeSelectAll = selectAll
      if (selectAll) {
        state.storeExcludeIds = []
      } else {
        state.storeSelectedIds = []
      }
    },
    SET_STORE_EXCLUDE_IDS(state, ids) {
      state.storeExcludeIds = cloneDeep(ids)
    },
    SET_STORE_SELECTED_IDS(state, ids) {
      state.storeSelectedIds = cloneDeep(ids)
    },
    TOGGLE_STORE_SELECTION(state, { storeId, selected }) {
      if (state.storeSelectAll) {
        // 全选模式：操作排除列表
        if (selected) {
          state.storeExcludeIds = state.storeExcludeIds.filter(
            (id) => id !== storeId,
          )
        } else if (!state.storeExcludeIds.includes(storeId)) {
          state.storeExcludeIds.push(storeId)
        }
      } else if (selected) {
        // 非全选模式：添加到选中列表
        if (!state.storeSelectedIds.includes(storeId)) {
          state.storeSelectedIds.push(storeId)
        }
      } else {
        // 非全选模式：从选中列表移除
        state.storeSelectedIds = state.storeSelectedIds.filter(
          (id) => id !== storeId,
        )
      }
    },

    // 预览数据
    SET_PREVIEW_DATA(state, data) {
      state.previewData = cloneDeep(data)
    },
    SET_PREVIEW_LIST(state, list) {
      state.previewList = cloneDeep(list)
    },

    // 枚举数据
    SET_ENUMS(state, { carAge, carMileage }) {
      state.enums.carAge = carAge || []
      state.enums.carMileage = carMileage || []
      state.enums.loaded = true
    },
  },

  actions: {
    // 初始化
    init({ commit }, options) {
      commit('INIT_STATE', options)
    },

    // 重置
    reset({ commit }) {
      commit('RESET_STATE')
    },

    // TODO: 获取 sessionId
    // async fetchSessionId({ commit, state }, params) {
    //   commit('SET_SESSION_LOADING', true)
    //   try {
    //     const res = await getSession(params)
    //     if (res.code === 1) {
    //       commit('SET_SESSION_ID', res.data.sessionId)
    //     }
    //   } finally {
    //     commit('SET_SESSION_LOADING', false)
    //   }
    // },

    // TODO: 释放 session
    // async disposeSession({ commit, state }) {
    //   if (!state.sessionId) return
    //   try {
    //     await disposeSession({ sessionId: state.sessionId })
    //   } catch (e) {
    //     console.error('dispose session failed:', e)
    //   }
    //   commit('SET_SESSION_ID', null)
    // },

    // 更新车龄
    updateCarAge({ commit }, carAge) {
      commit('SET_CAR_AGE', carAge)
    },

    // 切换车型选中状态
    toggleModelSelection({ commit }, payload) {
      commit('TOGGLE_MODEL_SELECTION', payload)
    },

    // 切换门店选中状态
    toggleStoreSelection({ commit }, payload) {
      commit('TOGGLE_STORE_SELECTION', payload)
    },
  },
}

export default batchPublish
